import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * Cart Drawer
 * Native Shopify / Savor behavior
 * + Ben's Custom enhancements
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {number | null} */
  #timerInterval = null;

  /** @type {AbortController | null} */
  #recommendationsController = null;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
      this.closest('theme-drawer')
    );
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    /**
     * Si le panier est déjà ouvert au chargement,
     * on initialise directement les éléments.
     */
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    this.#stopTimer();

    this.#recommendationsController?.abort();
  }

  /**
   * Appelé à chaque ouverture du drawer.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    /**
     * BEN'S CUSTOM
     */
    this.#initializeEnhancements();

    /**
     * Comportement Shopify d'origine.
     * Ferme le panier lorsque la fenêtre
     * des paiements en plusieurs fois est ouverte.
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this
        .querySelector('shopify-payment-terms')
        ?.shadowRoot?.querySelector('#shopify-installments-cta');

      cta?.addEventListener(
        'click',
        () => this.#themeDrawer?.close(),
        { once: true }
      );
    });
  };

  /**
   * Gestion native des mises à jour panier.
   *
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen =
      this.hasAttribute('auto-open') &&
      event.action === 'add' &&
      !this.#themeDrawer?.isOpen;

    const sourceModal =
      /** @type {HTMLDialogElement | null} */ (
        event.target instanceof Element
          ? event.target.closest('dialog:modal')
          : null
      );

    if (
      shouldAutoOpen &&
      !sourceModal &&
      !this.#isCartEmpty()
    ) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => {
          requestAnimationFrame(() => {
            this.#updateStickyState();

            /**
             * Le contenu du panier peut être remplacé
             * par Shopify après une modification.
             *
             * On réinitialise donc notre timer et
             * nos recommandations après le rendu.
             */
            this.#initializeEnhancements();
          });
        };

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) {
            this.#themeDrawer?.open();
          }

          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener(
            'close',
            openAndSettle,
            { once: true }
          );
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-drawer] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * BEN'S CUSTOM
   *
   * Initialise tous les éléments personnalisés.
   */
  #initializeEnhancements() {
    requestAnimationFrame(() => {
      this.#initializeTimer();
      this.#initializeRecommendations();
    });
  }

  /**
   * ============================================================
   * COMPTE À REBOURS
   * ============================================================
   */

  #initializeTimer() {
    const timer = this.querySelector(
      '[data-bc-cart-timer]'
    );

    if (!timer) {
      this.#stopTimer();
      return;
    }

    const display = timer.querySelector(
      '[data-bc-countdown]'
    );

    if (!display) {
      this.#stopTimer();
      return;
    }

    const minutes = Number.parseInt(
      timer.dataset.minutes || '10',
      10
    );

    const seconds = Number.parseInt(
      timer.dataset.seconds || '30',
      10
    );

    const durationSeconds =
      Math.max(0, minutes * 60 + seconds);

    const durationMilliseconds =
      durationSeconds * 1000;

    if (!durationMilliseconds) {
      return;
    }

    const storageKey =
      'bens-custom-cart-reservation-expiration';

    let expiration = 0;

    try {
      expiration = Number.parseInt(
        sessionStorage.getItem(storageKey) || '0',
        10
      );
    } catch {
      expiration = 0;
    }

    const now = Date.now();

    /**
     * Nouveau timer si :
     * - il n'existe pas
     * - il est expiré
     */
    if (!expiration || expiration <= now) {
      expiration =
        now + durationMilliseconds;

      try {
        sessionStorage.setItem(
          storageKey,
          String(expiration)
        );
      } catch {
        // SessionStorage indisponible :
        // le timer continue simplement sans stockage.
      }
    }

    const update = () => {
      let remaining =
        expiration - Date.now();

      /**
       * Une fois arrivé à zéro,
       * on affiche 00:00.
       *
       * On ne redémarre pas artificiellement
       * le compteur immédiatement.
       */
      if (remaining <= 0) {
        display.textContent = '00:00';

        this.#stopTimer();

        return;
      }

      const totalSeconds =
        Math.ceil(remaining / 1000);

      const remainingMinutes =
        Math.floor(totalSeconds / 60);

      const remainingSeconds =
        totalSeconds % 60;

      display.textContent =
        `${String(remainingMinutes).padStart(2, '0')}:` +
        `${String(remainingSeconds).padStart(2, '0')}`;
    };

    this.#stopTimer();

    update();

    this.#timerInterval =
      window.setInterval(update, 1000);
  }

  #stopTimer() {
    if (this.#timerInterval !== null) {
      window.clearInterval(
        this.#timerInterval
      );

      this.#timerInterval = null;
    }
  }

  /**
   * ============================================================
   * RECOMMANDATIONS SHOPIFY
   * ============================================================
   */

  async #initializeRecommendations() {
    const container = this.querySelector(
      '[data-bc-recommendations]'
    );

    if (!container) {
      return;
    }

    const grid = container.querySelector(
      '[data-bc-recommendations-grid]'
    );

    if (!grid) {
      return;
    }

    const productId =
      container.dataset.productId;

    const limit =
      Number.parseInt(
        container.dataset.limit || '4',
        10
      );

    if (!productId) {
      container.hidden = true;
      return;
    }

    /**
     * Ne recharge pas inutilement les mêmes
     * recommandations si elles sont déjà présentes.
     */
    if (
      grid.dataset.productId === productId &&
      grid.children.length > 0
    ) {
      return;
    }

    this.#recommendationsController?.abort();

    this.#recommendationsController =
      new AbortController();

    try {
      const url =
        `${window.Shopify?.routes?.root || '/'}recommendations/products.json` +
        `?product_id=${encodeURIComponent(productId)}` +
        `&limit=${encodeURIComponent(limit)}`;

      const response = await fetch(url, {
        signal:
          this.#recommendationsController.signal,
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(
          `Recommendations request failed: ${response.status}`
        );
      }

      const data = await response.json();

      const products = Array.isArray(data.products)
        ? data.products
        : [];

      if (!products.length) {
        container.hidden = true;
        return;
      }

      grid.innerHTML = '';

      for (const product of products) {
        const card =
          this.#createRecommendationCard(product);

        if (card) {
          grid.appendChild(card);
        }
      }

      if (!grid.children.length) {
        container.hidden = true;
        return;
      }

      grid.dataset.productId =
        productId;

      container.hidden = false;
    } catch (error) {
      if (error?.name === 'AbortError') {
        return;
      }

      console.warn(
        '[cart-drawer] Recommendations error:',
        error
      );

      container.hidden = true;
    }
  }

  /**
   * Construit une carte recommandation.
   */
  #createRecommendationCard(product) {
    if (!product?.url || !product?.title) {
      return null;
    }

    const article =
      document.createElement('article');

    article.className =
      'bc-recommendation-card';

    /**
     * IMAGE
     */
    const imageLink =
      document.createElement('a');

    imageLink.className =
      'bc-recommendation-card__image';

    imageLink.href =
      product.url;

    imageLink.setAttribute(
      'aria-label',
      product.title
    );

    const imageUrl =
      typeof product.featured_image === 'string'
        ? product.featured_image
        : product.featured_image?.url;

    if (imageUrl) {
      const image =
        document.createElement('img');

      image.src = imageUrl;
      image.alt = product.title;
      image.loading = 'lazy';

      imageLink.appendChild(image);
    }

    /**
     * TITRE
     */
    const title =
      document.createElement('a');

    title.className =
      'bc-recommendation-card__title';

    title.href =
      product.url;

    title.textContent =
      product.title;

    /**
     * BAS DE CARTE
     */
    const bottom =
      document.createElement('div');

    bottom.className =
      'bc-recommendation-card__bottom';

    const price =
      document.createElement('span');

    price.className =
      'bc-recommendation-card__price';

    price.textContent =
      this.#formatMoney(product.price);

    const productLink =
      document.createElement('a');

    productLink.className =
      'bc-recommendation-card__link';

    productLink.href =
      product.url;

    productLink.setAttribute(
      'aria-label',
      `Voir ${product.title}`
    );

    productLink.textContent = '+';

    bottom.appendChild(price);
    bottom.appendChild(productLink);

    article.appendChild(imageLink);
    article.appendChild(title);
    article.appendChild(bottom);

    return article;
  }

  /**
   * Affichage du prix selon la devise Shopify.
   */
  #formatMoney(cents) {
    const amount =
      Number(cents) / 100;

    if (!Number.isFinite(amount)) {
      return '';
    }

    const currency =
      window.Shopify?.currency?.active ||
      'EUR';

    const locale =
      document.documentElement.lang ||
      'fr-FR';

    try {
      return new Intl.NumberFormat(
        locale,
        {
          style: 'currency',
          currency,
        }
      ).format(amount);
    } catch {
      return `${amount.toFixed(2)} €`;
    }
  }

  /**
   * ============================================================
   * SHOPIFY D'ORIGINE
   * ============================================================
   */

  #isCartEmpty() {
    return Boolean(
      this.querySelector(
        '.cart-drawer--empty'
      )
    );
  }

  #updateStickyState() {
    const dialog =
      this.#dialog;

    if (!dialog) {
      return;
    }

    const content =
      dialog.querySelector(
        '.cart-drawer__content'
      );

    const summary =
      dialog.querySelector(
        '.cart-drawer__summary'
      );

    if (!content || !summary) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;
    }

    const drawerHeight =
      dialog
        .getBoundingClientRect()
        .height;

    const summaryHeight =
      summary
        .getBoundingClientRect()
        .height;

    if (!drawerHeight) {
      return;
    }

    const ratio =
      summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold
        ? 'false'
        : 'true'
    );
  }
}

if (
  !customElements.get(
    'cart-drawer-component'
  )
) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}